import { WineType, formatWineType } from '@wineryxplorer/shared';
import { FormSection } from '../ui/FormSection';
import type { WineFormErrors } from './types';

export interface WineBasicInfoFieldsProps {
  name: string;
  onNameChange: (value: string) => void;
  wineType: WineType;
  onWineTypeChange: (value: WineType) => void;
  vintage: string;
  onVintageChange: (value: string) => void;
  varietal: string;
  onVarietalChange: (value: string) => void;
  countryOfOrigin: string;
  onCountryOfOriginChange: (value: string) => void;
  description: string;
  onDescriptionChange: (value: string) => void;
  errors: WineFormErrors;
  touched: Record<string, boolean>;
  onBlur: (field: string) => void;
  isSubmitting: boolean;
}

export const WineBasicInfoFields = ({
  name,
  onNameChange,
  wineType,
  onWineTypeChange,
  vintage,
  onVintageChange,
  varietal,
  onVarietalChange,
  countryOfOrigin,
  onCountryOfOriginChange,
  description,
  onDescriptionChange,
  errors,
  touched,
  onBlur,
  isSubmitting,
}: WineBasicInfoFieldsProps) => (
  <FormSection title="Basic Information">
    {/* Wine Name */}
    <div>
      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
        Wine Name <span className="text-red-500">*</span>
      </label>
      <input
        id="name"
        type="text"
        value={name}
        onChange={(e) => onNameChange(e.target.value)}
        onBlur={() => onBlur('name')}
        disabled={isSubmitting}
        placeholder="e.g., Cabernet Sauvignon Reserve"
        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-info-500 ${
          touched.name && errors.name ? 'border-red-500' : 'border-gray-300'
        } ${isSubmitting ? 'bg-gray-100 cursor-not-allowed' : ''}`}
        maxLength={200}
      />
      {touched.name && errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
    </div>

    {/* Wine Type */}
    <div>
      <label htmlFor="wineType" className="block text-sm font-medium text-gray-700 mb-1">
        Wine Type <span className="text-red-500">*</span>
      </label>
      <select
        id="wineType"
        value={wineType}
        onChange={(e) => onWineTypeChange(e.target.value as WineType)}
        onBlur={() => onBlur('wineType')}
        disabled={isSubmitting}
        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-info-500 ${
          touched.wineType && errors.wineType ? 'border-red-500' : 'border-gray-300'
        } ${isSubmitting ? 'bg-gray-100 cursor-not-allowed' : ''}`}
      >
        {Object.values(WineType).map((type) => (
          <option key={type} value={type}>
            {formatWineType(type)}
          </option>
        ))}
      </select>
    </div>

    {/* Vintage */}
    <div>
      <label htmlFor="vintage" className="block text-sm font-medium text-gray-700 mb-1">
        Vintage
      </label>
      <input
        id="vintage"
        type="text"
        inputMode="numeric"
        value={vintage}
        onChange={(e) => onVintageChange(e.target.value)}
        onBlur={() => onBlur('vintage')}
        disabled={isSubmitting}
        placeholder="e.g., 2020"
        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-info-500 ${
          touched.vintage && errors.vintage ? 'border-red-500' : 'border-gray-300'
        } ${isSubmitting ? 'bg-gray-100 cursor-not-allowed' : ''}`}
      />
      {touched.vintage && errors.vintage && (
        <p className="mt-1 text-sm text-red-600">{errors.vintage}</p>
      )}
    </div>

    {/* Varietal */}
    <div>
      <label htmlFor="varietal" className="block text-sm font-medium text-gray-700 mb-1">
        Varietal
      </label>
      <input
        id="varietal"
        type="text"
        value={varietal}
        onChange={(e) => onVarietalChange(e.target.value)}
        disabled={isSubmitting}
        placeholder="e.g., Cabernet Sauvignon"
        className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-info-500 ${
          isSubmitting ? 'bg-gray-100 cursor-not-allowed' : ''
        }`}
      />
    </div>

    {/* Country of Origin */}
    <div>
      <label htmlFor="countryOfOrigin" className="block text-sm font-medium text-gray-700 mb-1">
        Country of Origin <span className="text-red-500">*</span>
      </label>
      <input
        id="countryOfOrigin"
        type="text"
        value={countryOfOrigin}
        onChange={(e) => onCountryOfOriginChange(e.target.value)}
        onBlur={() => onBlur('countryOfOrigin')}
        disabled={isSubmitting}
        placeholder="e.g., USA"
        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-info-500 ${
          touched.countryOfOrigin && errors.countryOfOrigin ? 'border-red-500' : 'border-gray-300'
        } ${isSubmitting ? 'bg-gray-100 cursor-not-allowed' : ''}`}
      />
      {touched.countryOfOrigin && errors.countryOfOrigin && (
        <p className="mt-1 text-sm text-red-600">{errors.countryOfOrigin}</p>
      )}
    </div>

    {/* Description */}
    <div>
      <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
        Description
      </label>
      <textarea
        id="description"
        value={description}
        onChange={(e) => onDescriptionChange(e.target.value)}
        disabled={isSubmitting}
        placeholder="Describe the wine..."
        rows={4}
        className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-info-500 ${
          isSubmitting ? 'bg-gray-100 cursor-not-allowed' : ''
        }`}
      />
    </div>
  </FormSection>
);
